import { warehouseApi } from 'src/api/warehouse';
import { useDebounce } from 'src/hooks/useDebounce';
import { WarehouseProductDto } from 'woofwoof-api';
import { warehouseRequestOptions } from '../utils/request';
import {
  EMPTY_PRODUCT_FILTERS,
  PRODUCT_PICKER_LIMIT,
  buildProductListParams,
} from './useWarehouseProducts';
import { useWarehouseQuery } from './useWarehouseQuery';

const SEARCH_DEBOUNCE_MS = 300;

export interface ProductPickerPage {
  /** `undefined` until the first answer for this search. */
  products: WarehouseProductDto[] | undefined;
  isLoading: boolean;
}

/**
 * A search picker's first page of active Products (the Sale and demand
 * pickers): a 300 ms debounce on the trimmed search, key
 * `['warehouse-products', organizationId, keyPart, search]`, and no previous
 * data, so a clinic switch never offers the previous clinic's Products (S3).
 */
export function useProductPickerPage(
  keyPart: string,
  search: string,
  enabled: boolean,
): ProductPickerPage {
  const term = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const params = buildProductListParams(
    { ...EMPTY_PRODUCT_FILTERS, search: term },
    0,
    PRODUCT_PICKER_LIMIT,
  );
  const query = useWarehouseQuery(
    'warehouse-products',
    [keyPart, term],
    async (organizationId, signal) =>
      (
        await warehouseApi.warehouseProductControllerList(
          params,
          warehouseRequestOptions(organizationId, signal),
        )
      ).data,
    { enabled },
  );
  // Until the debounced search catches up with the typing, the last answer
  // is for other text: offering it would let Enter pick a stale Product.
  const pending = term !== search.trim();
  return {
    products: pending ? undefined : query.data?.data,
    isLoading: enabled && (pending || query.isInitialLoading),
  };
}
